/**
 * Reusable "suggested template" banner — one implementation shared by every
 * blog-creation surface (Content Planner wizard, Blog Command Center panel,
 * stepped wizard), so the behavior can't drift between them.
 *
 * Backed by the deterministic backend classifier (GET /blog-creation/templates/suggest).
 * Advisory only: the user's manual template pick always wins.
 */
import React from 'react';
import { Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSuggestedTemplate, useBlogTemplates } from '@/hooks/useBlogTemplates';
import { useDebounce } from '@/hooks/use-debounce';
import { TemplatePreviewTrigger } from '@/components/blog-templates/TemplatePreviewTrigger';

// Only surface a genuine, high-signal match. The classifier ALWAYS returns a
// template (falling back to default-seo-blog / a weak "standard"), so this gate
// stops the UI from showing "Suggested: Default SEO Blog" for every keyword.
const MIN_CONFIDENCE = 0.7;

export interface TemplateSuggestion {
  suggestedId: string | null;
  suggestedName: string | null;
  /** The debounced keyword actually queried (for display). */
  keyword: string;
}

/**
 * Shared suggestion logic: debounce the inputs, query the classifier, and gate
 * to strong matches. Surfaces that render an inline template grid can use this
 * directly (e.g. to badge the matching card); the banner below uses it too.
 */
export function useTemplateSuggestion(keyword: string, title?: string): TemplateSuggestion {
  const debouncedKeyword = useDebounce((keyword || '').trim(), 400);
  const debouncedTitle = useDebounce((title || '').trim(), 400);
  const { data } = useSuggestedTemplate(debouncedKeyword, debouncedTitle || undefined);

  const isStrong =
    data?.resolved_by === 'family-match' && (data?.confidence ?? 0) >= MIN_CONFIDENCE;
  const suggestedId = isStrong ? data?.template_id ?? null : null;

  return {
    suggestedId,
    suggestedName: suggestedId ? data?.template_name ?? null : null,
    keyword: debouncedKeyword,
  };
}

interface SuggestedTemplateBannerProps {
  keyword: string;
  title?: string;
  /** Currently-selected template id; the banner hides when the suggestion is already selected. */
  selectedTemplateId?: string | null;
  /** Apply the suggestion. Receives the suggested template id. */
  onSelect: (templateId: string) => void;
  className?: string;
}

export const SuggestedTemplateBanner: React.FC<SuggestedTemplateBannerProps> = ({
  keyword,
  title,
  selectedTemplateId,
  onSelect,
  className,
}) => {
  const { suggestedId, suggestedName, keyword: debouncedKeyword } = useTemplateSuggestion(
    keyword,
    title
  );
  // Look up the full template so the inline Preview can show its config.
  const { data: templatesData } = useBlogTemplates();
  const suggestedTemplate = suggestedId
    ? templatesData?.templates.find((t) => t.id === suggestedId) ?? null
    : null;

  if (!suggestedId || !suggestedName || suggestedId === selectedTemplateId) {
    return null;
  }

  return (
    <div
      className={cn(
        'w-full flex items-center gap-2 rounded-lg border border-teal/40 bg-teal-light px-3 py-2 text-sm',
        className
      )}
    >
      <Sparkles className="h-4 w-4 shrink-0 text-teal-dark" />
      <button
        type="button"
        onClick={() => onSelect(suggestedId)}
        className="min-w-0 flex-1 truncate text-left text-teal-dark hover:underline"
      >
        Suggested for &ldquo;{debouncedKeyword}&rdquo;:{' '}
        <span className="font-semibold">{suggestedName}</span>
      </button>
      {suggestedTemplate && (
        <TemplatePreviewTrigger
          template={suggestedTemplate}
          onUse={onSelect}
          isSelected={suggestedId === selectedTemplateId}
          className="shrink-0 text-teal-dark hover:bg-teal/10"
        />
      )}
      <button
        type="button"
        onClick={() => onSelect(suggestedId)}
        className="shrink-0 font-medium text-teal-dark hover:underline"
      >
        Use this
      </button>
    </div>
  );
};
